import { supabase } from "@/integrations/supabase/client";
import type { Department } from "@/lib/auth-context";

export const SUBMISSION_STATUSES = [
  "pending",
  "approved",
  "correction_needed",
  "rejected",
] as const;

export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export const STATUS_LABELS: Record<SubmissionStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  correction_needed: "Correction Needed",
  rejected: "Rejected",
};

export interface Submission {
  id: string;
  user_id: string;
  department: Department;
  submission_date: string;
  title: string;
  brand: string | null;
  product: string | null;
  details: Record<string, unknown>;
  status: SubmissionStatus;
  points: number;
  correction_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export type SubmissionInput = {
  title: string;
  brand?: string | null;
  product?: string | null;
  details: Record<string, unknown>;
};

export async function fetchMySubmissions(userId: string): Promise<Submission[]> {
  const { data, error } = await supabase
    .from("submissions")
    .select("*")
    .eq("user_id", userId)
    .order("submission_date", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Submission[];
}

export async function fetchBrandsAndProducts() {
  const [{ data: brands }, { data: products }] = await Promise.all([
    supabase.from("brands").select("name").eq("active", true).order("name"),
    supabase
      .from("products")
      .select("brand, name")
      .eq("active", true)
      .order("name"),
  ]);
  return {
    brands: (brands ?? []).map((b) => b.name),
    products: (products ?? []) as { brand: string; name: string }[],
  };
}

export async function insertSubmission(
  userId: string,
  department: Department,
  input: SubmissionInput,
): Promise<Submission> {
  const { data, error } = await supabase
    .from("submissions")
    .insert({
      user_id: userId,
      department,
      title: input.title,
      brand: input.brand ?? null,
      product: input.product ?? null,
      details: input.details as never,
    })
    .select()
    .single();
  if (error) throw error;
  return data as unknown as Submission;
}

export async function updateSubmission(
  id: string,
  input: SubmissionInput,
): Promise<Submission> {
  const { data, error } = await supabase
    .from("submissions")
    .update({
      title: input.title,
      brand: input.brand ?? null,
      product: input.product ?? null,
      details: input.details as never,
    })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as Submission;
}

export async function deleteSubmission(id: string) {
  const { error } = await supabase.from("submissions").delete().eq("id", id);
  if (error) throw error;
}
